import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Svařování - V1 DRAFT - authored for structural completeness; NOT a substitute
 * for sign-off by a Czech osoba odborně způsobilá v prevenci rizik. Legal refs
 * per the Czech BOZP research (vyhl. 87/2000 Sb. §3 odst. 10 písm. d) + §4; ČSN 05 0601;
 * vyhl. 180/2015 Sb. §5(2)). Czech-only (LocalizedText cs-CZ). Three chapters
 * (požární dohled | odsávání zplodin | ochrana zraku) + a graded quiz with one
 * question per kind. Answer keys live here (repo-privileged) and in the DB;
 * stripped from the client payload by the API.
 */
export const weldingProfile: TrainingProfile = {
  slug: 'welding',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Svařování',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-pozarni-dohled',
        title: t('Požární dohled a požární dozor', 'Fire watch and fire supervision'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-dohled'],
        blocks: [
          {
            id: 'h-dohled',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Svařování jako činnost se zvýšeným požárním nebezpečím',
              'Welding as an activity with an increased fire risk',
            ),
          },
          {
            id: 'p-dohled-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Svařování je **činnost se zvýšeným nebezpečím vzniku požáru**. Rozžhavené okuje a jiskry mohou doletět **až 10 m** a způsobit požár i s časovým zpožděním. Pracoviště musí být před zahájením posouzeno a zajištěno.',
              'Welding is an **activity with an increased risk of fire**. Red-hot scale and sparks can travel **up to 10 m** and start a fire even with a time delay. The workplace must be assessed and secured before work begins.',
            ),
          },
          {
            id: 'c-dohled-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní a normový základ: **vyhl. 87/2000 Sb. §3 odst. 10 písm. d)** (svářeč musí prokázat odbornou způsobilost), **§4** (zásady zabezpečení pracoviště při svařování) a **ČSN 05 0601** (bezpečnostní ustanovení pro svařování).',
              'Legal and standards basis: **Decree 87/2000 Coll. §3(10)(d)** (the welder must prove competence), **§4** (principles for securing the workplace during welding) and **ČSN 05 0601** (safety provisions for welding).',
            ),
          },
          {
            id: 'cl-dohled',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Odstranění hořlavých látek z okolí pracoviště',
                'Remove flammable materials from around the workplace',
              ),
              t(
                'Zakrytí nehořlavou clonou tam, kde nelze hořlaviny odstranit',
                'Cover with a non-combustible screen where flammables cannot be removed',
              ),
              t(
                'Funkční hasicí přístroj v dosahu pracoviště',
                'A working fire extinguisher within reach of the workplace',
              ),
              t(
                'Zajištění požárního dohledu po skončení práce',
                'Provide a fire watch after work is finished',
              ),
            ],
          },
          {
            id: 'anim-dohled',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'fireWatch',
            steps: [
              {
                label: t('Svařování končí', 'Welding ends'),
                say: t('Práce skončila, riziko ne.', 'The job is over, the risk is not.'),
              },
              {
                label: t('Dohled běží dál', 'The fire watch keeps running'),
                say: t(
                  'Požární dohled nejméně 8 hodin po skončení svařování (vyhl. 87/2000 Sb.).',
                  'Fire watch for at least 8 hours after welding ends (Decree 87/2000).',
                ),
              },
              {
                label: t('Skryté doutnání', 'Hidden smoulder'),
                say: t(
                  'Zachyceno včas - proto dohled.',
                  'Caught in time - that is what the watch is for.',
                ),
              },
            ],
            caption: t(
              'Po skončení svařování drž požární dohled - skryté doutnání se projeví i po hodinách.',
              'Keep a fire watch after welding - hidden smouldering can flare up even hours later.',
            ),
          },
          {
            id: 'ord-dohled',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky zabezpečení pracoviště před svařováním do správného pořadí.',
              'Order the steps for securing the workplace before welding.',
            ),
            items: [
              {
                id: 'od-1',
                label: t(
                  'Posouzení požárního nebezpečí pracoviště',
                  'Assess the workplace fire risk',
                ),
              },
              {
                id: 'od-2',
                label: t(
                  'Odstranění nebo zakrytí hořlavých látek',
                  'Remove or cover flammable materials',
                ),
              },
              {
                id: 'od-3',
                label: t(
                  'Příprava hasicího přístroje a zajištění dohledu',
                  'Prepare a fire extinguisher and arrange the fire watch',
                ),
              },
              { id: 'od-4', label: t('Zahájení svařování', 'Start welding') },
              {
                id: 'od-5',
                label: t('Požární dohled po skončení práce', 'Fire watch after work is finished'),
              },
            ],
          },
          {
            id: 'c-dohled-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Po skončení svařování zajisti **požární dohled po dobu nejméně 8 hodin** (běžně uváděná lhůta dle požárních předpisů). Skryté doutnání se může projevit požárem až po odchodu z pracoviště.',
              'After welding, provide a **fire watch for at least 8 hours** (the period commonly cited by fire-safety regulations). Hidden smouldering can break out into a fire only after you have left the workplace.',
            ),
          },
          {
            id: 'k-dohled',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Bez **posouzení pracoviště, hasicího přístroje a požárního dohledu** se svařovat nesmí. Tísňová linka hasičů: **150**.',
              'Never weld without a **workplace assessment, a fire extinguisher and a fire watch**. Fire brigade emergency line: **150**.',
            ),
          },
        ],
      },
      {
        id: 'ch-odsavani',
        title: t(
          'Odsávání zplodin a ochrana dýchacích cest',
          'Fume extraction and respiratory protection',
        ),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-odsavani'],
        blocks: [
          {
            id: 'h-odsavani',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Svařovací dýmy a zplodiny', 'Welding fumes and combustion products'),
          },
          {
            id: 'p-odsavani-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při svařování vznikají **jemné dýmy a plyny** obsahující kovové oxidy. Zvlášť nebezpečný je **mangan** a **šestimocný chrom (Cr VI)** z nerezových ocelí - Cr VI je **karcinogen**. Přednost má vždy **místní odsávání** přímo u zdroje.',
              'Welding produces **fine fumes and gases** containing metal oxides. Particularly dangerous are **manganese** and **hexavalent chromium (Cr VI)** from stainless steels - Cr VI is a **carcinogen**. **Local extraction** right at the source always takes priority.',
            ),
          },
          {
            id: 'acc-odsavani',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t(
                  'Proč nestačí celkové větrání místnosti?',
                  'Why is general room ventilation not enough?',
                ),
                a: t(
                  'Celkové větrání dýmy pouze ředí, ale nezachytí je u zdroje. Místní (lokální) odsávání odvádí zplodiny dřív, než se dostanou do dýchací zóny svářeče.',
                  "General ventilation only dilutes the fumes but does not capture them at the source. Local extraction removes the fumes before they reach the welder's breathing zone.",
                ),
              },
              {
                q: t(
                  'Kdy je nutná ochrana dýchacích cest?',
                  'When is respiratory protection required?',
                ),
                a: t(
                  'Tam, kde odsávání nelze dostatečně zajistit (uzavřené a stísněné prostory, svařování nerezu, pozinkovaných dílů), se použije filtračně-ventilační jednotka nebo přívod čistého vzduchu.',
                  'Where extraction cannot be adequately provided (confined and cramped spaces, welding stainless steel or galvanised parts), use a powered air-purifying respirator or a supplied clean-air feed.',
                ),
              },
            ],
          },
          {
            id: 'c-odsavani-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Svařování **pozinkovaných** dílů uvolňuje oxid zinečnatý a hrozí **horečka z kovových dýmů**. V uzavřených prostorech hrozí i vytěsnění kyslíku ochrannými plyny (CO₂, argon).',
              'Welding **galvanised** parts releases zinc oxide and risks **metal fume fever**. In confined spaces there is also a risk of oxygen displacement by shielding gases (CO₂, argon).',
            ),
          },
          {
            id: 'kc-odsavani',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Který způsob odvodu zplodin má při svařování přednost?',
              'Which method of fume removal takes priority during welding?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t('Místní odsávání u zdroje', 'Local extraction at the source'),
              },
              {
                id: 'kc-b',
                label: t(
                  'Otevřené okno na druhé straně dílny',
                  'An open window on the other side of the workshop',
                ),
              },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Místní odsávání zachytí dýmy dřív, než se dostanou do dýchací zóny svářeče.',
              "Local extraction captures the fumes before they reach the welder's breathing zone.",
            ),
          },
        ],
      },
      {
        id: 'ch-zrak',
        title: t('Ochrana zraku a obličeje', 'Eye and face protection'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-zrak',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Záření a ochrana očí', 'Radiation and eye protection'),
          },
          {
            id: 'p-zrak-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Elektrický oblouk vyzařuje intenzivní **ultrafialové a infračervené záření**. Nechráněné oči utrpí **zánět spojivek a rohovky (tzv. oslnění svářečů)** - bolestivý stav, který se projeví až s několikahodinovým zpožděním. Nutná je svářečská kukla s **filtrem o správném stupni zatemnění**.',
              "The electric arc emits intense **ultraviolet and infrared radiation**. Unprotected eyes suffer **inflammation of the conjunctiva and cornea (welder's flash / arc eye)** - a painful condition that appears only after a delay of several hours. A welding helmet with a **filter of the correct shade** is essential.",
            ),
          },
          {
            id: 'c-zrak-norma',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Stupeň zatemnění filtru se volí podle metody a proudu dle **ČSN EN ISO 16321-2** (nahradila ČSN EN 169 k 30. 4. 2025); pro obloukové svařování se běžně používá stupeň přibližně **9 až 13**. Vyšší proud vyžaduje vyšší stupeň.',
              'The filter shade is selected according to the process and the current per **ČSN EN ISO 16321-2** (which replaced ČSN EN 169 on 30 Apr 2025); for arc welding a shade of approximately **9 to 13** is commonly used. A higher current requires a higher shade.',
            ),
          },
          {
            id: 'cl-zrak',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Svářečská kukla nebo štít s filtrem odpovídajícího stupně',
                'A welding helmet or shield with a filter of the appropriate shade',
              ),
              t(
                'Ochrana okolních pracovníků clonou proti oslnění',
                'Protect nearby workers with an anti-glare screen',
              ),
              t(
                'Ochranné brýle při odstraňování strusky a broušení',
                'Safety goggles when removing slag and grinding',
              ),
              t(
                'Kontrola neporušenosti filtru a krycího skla',
                'Check the integrity of the filter and the cover lens',
              ),
            ],
          },
          {
            id: 'k-zrak',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Nikdy se nedívej do oblouku **bez filtru správného stupně zatemnění**. Oslnění svářečů přichází opožděně a postihuje i přihlížející.',
              "Never look into the arc **without a filter of the correct shade**. Welder's flash comes on with a delay and affects bystanders too.",
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-dohled-single',
      kind: 'single',
      chapterId: 'ch-pozarni-dohled',
      prompt: t(
        'Co musí být zajištěno po skončení svařování?',
        'What must be provided after welding is finished?',
      ),
      choices: [
        { id: 'a', label: t('Okamžitý odchod z pracoviště', 'Leaving the workplace immediately') },
        {
          id: 'b',
          label: t('Požární dohled po stanovenou dobu', 'A fire watch for the specified period'),
        },
        { id: 'c', label: t('Pouze uzamčení dílny', 'Just locking up the workshop') },
      ],
      correct: 'b',
    },
    {
      id: 'q-zrak-tf',
      kind: 'truefalse',
      chapterId: 'ch-zrak',
      prompt: t(
        'Do elektrického oblouku se lze krátce podívat bez ochranného filtru.',
        'You can briefly look into the electric arc without a protective filter.',
      ),
      correct: false,
    },
    {
      id: 'q-odsavani-multi',
      kind: 'multi',
      chapterId: 'ch-odsavani',
      prompt: t(
        'Které látky patří mezi nebezpečné složky svařovacích dýmů? (vyber všechny správné)',
        'Which substances are among the dangerous components of welding fumes? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Mangan', 'Manganese') },
        { id: 'b', label: t('Šestimocný chrom (Cr VI)', 'Hexavalent chromium (Cr VI)') },
        { id: 'c', label: t('Vodní pára', 'Water vapour') },
        {
          id: 'd',
          label: t('Oxid zinečnatý z pozinkovaných dílů', 'Zinc oxide from galvanised parts'),
        },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-dohled-numeric',
      kind: 'numeric',
      chapterId: 'ch-pozarni-dohled',
      prompt: t(
        'Po kolik hodin se nejméně zajišťuje požární dohled po skončení svařování?',
        'For at least how many hours must a fire watch be provided after welding is finished?',
      ),
      correct: 8,
      tolerance: 0,
    },
    {
      id: 'q-dohled-order',
      kind: 'ordering',
      chapterId: 'ch-pozarni-dohled',
      prompt: t(
        'Seřaď kroky zabezpečení pracoviště před svařováním.',
        'Order the steps for securing the workplace before welding.',
      ),
      choices: [
        { id: 'a', label: t('Posouzení požárního nebezpečí', 'Assess the fire risk') },
        { id: 'b', label: t('Odstranění hořlavin', 'Remove flammables') },
        { id: 'c', label: t('Příprava hasicího přístroje', 'Prepare a fire extinguisher') },
        { id: 'd', label: t('Zahájení svařování', 'Start welding') },
        { id: 'e', label: t('Požární dohled po skončení', 'Fire watch after finishing') },
      ],
      correct: ['a', 'b', 'c', 'd', 'e'],
    },
  ],
};
